import React, {useEffect, useState} from 'react';
import {Avatar, Box, Dialog, IconButton, InputBase, Typography} from '@mui/material';
import {CloseOutlined, EditOutlined, ThumbDownAltOutlined, ThumbUpAltOutlined} from '@mui/icons-material';
import ReactPlayer from 'react-player';
import moment from 'moment';
import {useTranslate} from 'react-admin';
import {t} from '../themes/statamicTokens';
import PrimaryButton from '../components/statamic/PrimaryButton';
import StatusPill from '../components/statamic/StatusPill';
import TranslationTable from '../components/TranslationTable';

/**
 * Detail of one pending post in the moderation queue (#51): the media, the original text and the
 * translations per language, with Approve and Reject; the text can be edited before approval (#52).
 * The pending routes already carry `translations`, `name` and `ext`, so the dialog needs no extra request.
 */

const SectionLabel = ({children}) => (
    <Typography
        sx={{
            fontSize: `${t.type.caption.size}px`,
            fontWeight: 600,
            color: t.color.text.secondary,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            mb: 1,
        }}
    >
        {children}
    </Typography>
);

const PostMedia = ({post}) => {
    const translate = useTranslate();
    const [failed, setFailed] = useState(false);
    const isVideo = post.media_type === 'video' || post.message_type === 'VIDEO';
    const isPhoto = post.media_type === 'photo' || post.message_type === 'PHOTO';
    if (post.message_type === 'TEXT' || !post.name || !post.ext || !(isPhoto || isVideo)) return null;
    const src = `${process.env.REACT_APP_CDN_URL}/${post.name}.${post.ext}`;

    if (failed) {
        return (
            <Typography sx={{fontSize: `${t.type.small.size}px`, color: t.color.text.muted, mb: 2}}>
                {translate('Media could not be loaded')}
            </Typography>
        );
    }

    return (
        <Box sx={{borderRadius: `${t.radius.sm}px`, overflow: 'hidden', backgroundColor: '#000', mb: 2}}>
            {isPhoto && (
                <Box
                    component="img"
                    src={src}
                    alt=""
                    onError={() => setFailed(true)}
                    sx={{width: '100%', maxHeight: 420, objectFit: 'contain', display: 'block'}}
                />
            )}
            {isVideo && <ReactPlayer url={src} controls width="100%" height="auto" onError={() => setFailed(true)}/>}
        </Box>
    );
};

/** The post's text, with an edit mode for the moderator (#52). */
const PostText = ({post, editing, draft, setDraft, saving, error, onEdit, onCancel, onSave, pending}) => {
    const translate = useTranslate();
    const trimmed = draft.trim();
    const canSave = !saving && trimmed.length > 0 && trimmed !== (post.description ?? '').trim();

    return (
        <Box sx={{mb: 2.5}}>
            <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1}}>
                <SectionLabel>{translate('Original text')}</SectionLabel>
                {!editing && (
                    <PrimaryButton
                        variant="secondary"
                        disabled={pending}
                        startIcon={<EditOutlined sx={{fontSize: 14}}/>}
                        onClick={onEdit}
                        sx={{minHeight: 28, px: 1, mb: 1, fontSize: `${t.type.caption.size}px`}}
                    >
                        {translate('Edit text')}
                    </PrimaryButton>
                )}
            </Box>
            {editing ? (
                <>
                    <InputBase
                        multiline
                        minRows={3}
                        fullWidth
                        autoFocus
                        value={draft}
                        disabled={saving}
                        onChange={(e) => setDraft(e.target.value)}
                        inputProps={{'aria-label': translate('Post text')}}
                        sx={{
                            fontSize: `${t.type.body.size}px`,
                            lineHeight: 1.55,
                            border: `1px solid ${error ? t.color.status.dangerSolid : t.color.border.default}`,
                            borderRadius: `${t.radius.sm}px`,
                            px: 1.5,
                            py: 1,
                            '&.Mui-focused': {borderColor: t.color.accent.primary},
                        }}
                    />
                    {error && (
                        <Typography role="alert" sx={{mt: 0.75, fontSize: `${t.type.small.size}px`, color: t.color.status.dangerSolid}}>
                            {error}
                        </Typography>
                    )}
                    <Typography sx={{mt: 0.75, fontSize: `${t.type.caption.size}px`, color: t.color.text.muted}}>
                        {translate('Saving translates the new text again for every language.')}
                    </Typography>
                    <Box sx={{display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1}}>
                        <PrimaryButton variant="secondary" disabled={saving} onClick={onCancel}>
                            {translate('Cancel')}
                        </PrimaryButton>
                        <PrimaryButton variant="primary" disabled={!canSave} onClick={onSave}>
                            {saving ? translate('Saving…') : translate('Save text')}
                        </PrimaryButton>
                    </Box>
                </>
            ) : (
                <Typography
                    sx={{
                        fontSize: `${t.type.body.size}px`,
                        color: post.description ? t.color.text.primary : t.color.text.muted,
                        lineHeight: 1.55,
                        whiteSpace: 'pre-wrap',
                    }}
                >
                    {post.description || '—'}
                </Typography>
            )}
        </Box>
    );
};

/**
 * One post's detail. Keyed by the post id, so the edit state never carries over to another post.
 * After a save the API's item is shown until the queue's refresh brings the same post back.
 */
const PostDetail = ({post: listPost, onClose, onApprove, onReject, onSaveText, onLockChange, pending}) => {
    const translate = useTranslate();
    const [saved, setSaved] = useState(null);
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    // A newer list item replaces the saved response.
    // A list item with the saved text replaces the response; an older fetch that lands late does not.
    useEffect(() => { setSaved(s => (s && listPost.description === s.description ? null : s)); }, [listPost]);

    const post = saved ?? listPost;
    const translations = post.translations ?? [];
    const busy = pending || editing || saving;
    // While saving, or while a changed text is open, the dialog cannot be closed by Escape, the
    // backdrop or the close button: the moderator saves or cancels first, and a save's error stays visible.
    const dirty = editing && draft.trim() !== (post.description ?? '').trim();
    const locked = saving || dirty;
    useEffect(() => {
        onLockChange?.(locked);
        return () => onLockChange?.(false);
    }, [locked, onLockChange]);

    const startEdit = () => {
        setDraft(post.description ?? '');
        setError(null);
        setEditing(true);
    };
    const cancelEdit = () => {
        setEditing(false);
        setError(null);
    };
    const save = () => {
        setSaving(true);
        setError(null);
        Promise.resolve(onSaveText(post, draft.trim()))
            .then((item) => {
                if (item) setSaved({...post, ...item});
                setEditing(false);
            })
            // Keep the moderator's edit in the field so they can retry; the API's message says why.
            .catch((e) => setError(e?.message || translate('The text could not be saved')))
            .finally(() => setSaving(false));
    };

    return (
        <>
                    <Box sx={{display: 'flex', alignItems: 'flex-start', gap: 1, px: 3, pt: 2.5, pb: 1.5}}>
                        <Box sx={{flex: 1, minWidth: 0}}>
                            <Typography
                                id="moderation-post-dialog-title"
                                sx={{fontSize: `${t.type.h3.size}px`, fontWeight: 600, color: t.color.text.primary, mb: 1}}
                            >
                                {translate('Post details')}
                            </Typography>
                            <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mb: 1}}>
                                <StatusPill variant="warning">{translate('Pending')}</StatusPill>
                                <StatusPill variant="neutral">
                                    {post.message_type || String(post.media_type ?? 'UNKNOWN').toUpperCase()}
                                </StatusPill>
                                {post.is_project_post && <StatusPill variant="info">{translate('Project Post')}</StatusPill>}
                                <Typography sx={{fontSize: `${t.type.caption.size}px`, color: t.color.text.muted}}>
                                    {moment(post.created_time).fromNow()}
                                </Typography>
                            </Box>
                            <Box sx={{display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap'}}>
                                <Avatar
                                    src={post.creator?.avatar_thumbnail
                                        ? `${process.env.REACT_APP_CDN_URL}/${post.creator.avatar_thumbnail}`
                                        : undefined}
                                    sx={{width: 22, height: 22, bgcolor: t.color.sidebarHover, fontSize: 10}}
                                >
                                    {post.creator?.given_name?.charAt(0)}
                                </Avatar>
                                <Typography sx={{fontSize: `${t.type.small.size}px`, color: t.color.text.primary, fontWeight: 500}}>
                                    {post.creator?.given_name} {post.creator?.family_name}
                                </Typography>
                                <Typography sx={{fontSize: `${t.type.small.size}px`, color: t.color.text.muted}}>·</Typography>
                                <Typography sx={{fontSize: `${t.type.small.size}px`, color: t.color.text.secondary}}>
                                    {post.child?.first_name} {post.child?.last_name}
                                </Typography>
                                {post.child?.charity?.charity_name && (
                                    <>
                                        <Typography sx={{fontSize: `${t.type.small.size}px`, color: t.color.text.muted}}>·</Typography>
                                        <Typography sx={{fontSize: `${t.type.small.size}px`, color: t.color.text.muted}}>
                                            {post.child.charity.charity_name}
                                        </Typography>
                                    </>
                                )}
                            </Box>
                        </Box>
                        <IconButton
                            aria-label={translate('Close')}
                            onClick={onClose}
                            disabled={pending || locked}
                            size="small"
                        >
                            <CloseOutlined sx={{fontSize: 18}}/>
                        </IconButton>
                    </Box>

                    <Box sx={{px: 3, pb: 2, maxHeight: '65vh', overflowY: 'auto'}}>
                        <PostMedia key={post.id} post={post}/>

                        <PostText
                            post={post}
                            editing={editing}
                            draft={draft}
                            setDraft={setDraft}
                            saving={saving}
                            error={error}
                            onEdit={startEdit}
                            onCancel={cancelEdit}
                            onSave={save}
                            pending={pending}
                        />

                        <SectionLabel>{translate('Translations')}</SectionLabel>
                        {translations.length > 0 ? (
                            <TranslationTable translations={translations}/>
                        ) : (
                            <Typography sx={{fontSize: `${t.type.small.size}px`, color: t.color.text.muted}}>
                                {translate('No translations for this post')}
                            </Typography>
                        )}

                        {post.is_project_post && (
                            <Typography sx={{mt: 2, fontSize: `${t.type.caption.size}px`, color: t.color.accent.primary, fontStyle: 'italic'}}>
                                {translate('Approving duplicates this post to all children in the project.')}
                            </Typography>
                        )}
                    </Box>

                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: 1,
                            px: 3,
                            py: 2,
                            borderTop: `1px solid ${t.color.border.hairline}`,
                        }}
                    >
                        <PrimaryButton
                            variant="secondary"
                            disabled={busy}
                            startIcon={<ThumbDownAltOutlined sx={{fontSize: 14}}/>}
                            onClick={onReject}
                            sx={{color: t.color.status.dangerSolid}}
                        >
                            {translate('Reject')}
                        </PrimaryButton>
                        <PrimaryButton
                            variant="primary"
                            disabled={busy}
                            startIcon={<ThumbUpAltOutlined sx={{fontSize: 14}}/>}
                            onClick={onApprove}
                        >
                            {translate('Approve')}
                        </PrimaryButton>
                    </Box>
        </>
    );
};

const ModerationPostDialog = ({post, onClose, onApprove, onReject, onSaveText, pending}) => {
    const [locked, setLocked] = useState(false);

    return (
        <Dialog
            open={Boolean(post)}
            onClose={pending || locked ? undefined : onClose}
            fullWidth
            maxWidth="md"
            aria-labelledby="moderation-post-dialog-title"
            slotProps={{
                paper: {
                    sx: {
                        borderRadius: `${t.radius.lg}px`,
                        border: `1px solid ${t.color.border.default}`,
                        boxShadow: t.shadow.modal,
                    },
                },
            }}
        >
            {post && (
                <PostDetail
                    key={post.id}
                    post={post}
                    onClose={onClose}
                    onApprove={onApprove}
                    onReject={onReject}
                    onSaveText={onSaveText}
                    onLockChange={setLocked}
                    pending={pending}
                />
            )}
        </Dialog>
    );
};

export default ModerationPostDialog;
